import React, { createContext, useState, useContext } from 'react';

// Создаем контекст
const GameContext = createContext();

// Провайдер контекста, который будет содержать все состояние игры
export const GameProvider = ({ children }) => {
  // Основное состояние игры
  const [gameState, setGameState] = useState({
    mission: 0, // Текущая миссия
    progress: 0, // Прогресс в процентах
    inventory: [], // Инвентарь (инструменты, ключи и т.д.)
    unlockedCommands: ['help', 'about', 'start', 'clear', 'status'], // Доступные команды
    logs: [], // История действий
    secrets: {}, // Найденные секреты
  });

  // Сохранение прогресса в localStorage
  const saveProgress = () => {
    try {
      localStorage.setItem('cipher_hunt_state', JSON.stringify(gameState));
    } catch (error) {
      console.error('Ошибка сохранения прогресса:', error);
    }
  };

  // Загрузка прогресса из localStorage
  const loadProgress = () => {
    try {
      const savedState = localStorage.getItem('cipher_hunt_state');
      if (savedState) {
        setGameState(JSON.parse(savedState));
        return true;
      }
    } catch (error) {
      console.error('Ошибка загрузки прогресса:', error);
    }
    return false;
  };

  // Обновление состояния игры
  const updateGameState = (updates) => {
    setGameState(prev => {
      const newState = { ...prev, ...updates };
      // Автоматически сохраняем после обновления
      setTimeout(() => {
        try {
          localStorage.setItem('cipher_hunt_state', JSON.stringify(newState));
        } catch (error) {
          console.error('Ошибка автосохранения:', error);
        }
      }, 0);
      return newState;
    });
  };

  // Добавление предмета в инвентарь
  const addToInventory = (item) => {
    updateGameState({
      inventory: [...gameState.inventory, item]
    });
  };

  // Разблокировка новой команды
  const unlockCommand = (command) => {
    if (!gameState.unlockedCommands.includes(command)) {
      updateGameState({
        unlockedCommands: [...gameState.unlockedCommands, command]
      });
    }
  };

  // Обновление прогресса миссии
  const advanceMission = (missionNumber, progressPercentage) => {
    updateGameState({
      mission: missionNumber,
      progress: progressPercentage
    });
  };

  // Добавление записи в лог
  const addToLogs = (logEntry) => {
    updateGameState({
      logs: [...gameState.logs, { timestamp: new Date().toISOString(), ...logEntry }]
    });
  };

  // Регистрация найденного секрета
  const discoverSecret = (secretId, secretData) => {
    updateGameState({
      secrets: { ...gameState.secrets, [secretId]: secretData }
    });
  };

  // Сброс игры (для отладки)
  const resetGame = () => {
    setGameState({
      mission: 0,
      progress: 0,
      inventory: [],
      unlockedCommands: ['help', 'about', 'start', 'clear', 'status'],
      logs: [],
      secrets: {},
    });
    localStorage.removeItem('cipher_hunt_state');
  };

  // Объект с функциями и состоянием для предоставления через контекст
  const value = {
    gameState,
    updateGameState,
    saveProgress,
    loadProgress,
    addToInventory,
    unlockCommand,
    advanceMission,
    addToLogs,
    discoverSecret,
    resetGame
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};

// Хук для использования контекста игры в компонентах
export const useGameContext = () => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGameContext must be used within a GameProvider');
  }
  return context;
};

export default GameContext; 